import { CellContext, ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PublicationTypesEnum, { DocumentTypesEnum } from '../../../core/enums/PublicationTypesEnum';
import { iPublication } from '../../../core/models/Publication';
import { iTag } from '../../../core/models/Tag';
import { IconFilter, IconFilterGray, IconFilterRed, IconDeny } from '../../../icons';
import { highlightSearchText } from '../../../core/helpers';
import Select from '../../../components/Select/Select';
import SortableHeader from '../../../components/DataTable/SortTableHeader/SortTableHeader';
import DataTable from '../../../components/DataTable/DataTable';
import useWindowSizeDebounced from '../../../core/hooks/useWindowSizeDebounced';
import RequestAccessButton from '../../../components/RequestAccessButton/RequestAccessButton';
import Button from '../../../components/Button/Button';
import { DEFAULT_TYPES } from '../Library';
import TagsCell from '../../../components/DataTable/TagsCell/TagsCell';
import { getDescriptionSnippet } from '../../../components/DataTable/DataTable.helpers';
import { CountriesEnum, GlobalContext } from '../../../GlobalContext';

interface TableProps {
    data: iPublication[];
    onRowClick?: (publication: iPublication) => void;
    onTagClick?: (tag: iTag) => void;
    isLoading?: boolean;
    searchQuery?: string;
    orderBy?: string;
    sortOrder?: string;
    onSort?: (orderBy: string, sortOrder: string) => void;
    typeFilter?: DocumentTypesEnum[];
    onTypeFilterChange?: (types: DocumentTypesEnum[]) => void;
    stickyTopOffset?: number;
}

const TYPE_OPTIONS = [
    { id: DocumentTypesEnum.Publication, title: 'Publication' },
    { id: DocumentTypesEnum.Mac, title: 'MAC' },
    { id: DocumentTypesEnum.Chapter, title: 'Chapter' }
];

const Table: React.FC<TableProps> = ({
    data,
    onRowClick,
    onTagClick,
    isLoading = false,
    searchQuery,
    orderBy,
    sortOrder,
    onSort,
    typeFilter = DEFAULT_TYPES,
    onTypeFilterChange,
    stickyTopOffset
}) => {
    const { selectedCountryId } = useContext(GlobalContext);

    const typeOptionsForCountry = useMemo(
        () =>
            selectedCountryId === CountriesEnum.Global
                ? TYPE_OPTIONS.filter((o) => o.id !== DocumentTypesEnum.Mac)
                : TYPE_OPTIONS,
        [selectedCountryId]
    );

    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // Manage immediate loading indicator when type filter changes
    const [isTypeChanging, setIsTypeChanging] = useState(false);
    const prevTypeFilterRef = useRef<DocumentTypesEnum[]>(typeFilter);

    // Detect typeFilter change
    useEffect(() => {
        const prev = prevTypeFilterRef.current;
        const isDifferent = prev.length !== typeFilter.length || !prev.every((t) => typeFilter.includes(t));
        if (isDifferent) {
            setIsTypeChanging(true);
        }
        prevTypeFilterRef.current = typeFilter;
    }, [typeFilter]);

    useEffect(() => {
        if (!isLoading) {
            const id = setTimeout(() => setIsTypeChanging(false), 100);
            return () => clearTimeout(id);
        }
    }, [isLoading]);

    const toggleRowExpansion = useCallback((publicationId: number) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(publicationId)) {
                newSet.delete(publicationId);
            } else {
                newSet.add(publicationId);
            }
            return newSet;
        });
    }, []);

    const isTypeFilterActive = useMemo(() => {
        if (!typeFilter) return false;
        return typeFilter.length !== DEFAULT_TYPES.length || !DEFAULT_TYPES.every((t) => typeFilter.includes(t));
    }, [typeFilter]);

    const showGrayIconWhenFocused = (isOpen: boolean) => {
        if (isOpen) {
            return <IconFilterGray className="Table__filter-icon" />;
        }
        return <IconFilter />;
    };

    const TypeHeader: React.FC = useCallback(() => {
        return (
            <Select
                inputButton
                buttonContent={(isOpen: boolean) => (
                    <span className={`Table__type-select`}>
                        TYPE {isTypeFilterActive ? <IconFilterRed /> : showGrayIconWhenFocused(isOpen)}
                    </span>
                )}
                options={typeOptionsForCountry as any}
                value={typeFilter as any}
                onChange={(ids: (string | number)[]) => onTypeFilterChange?.(ids as unknown as DocumentTypesEnum[])}
                closeOnSelect={false}
                allowUnselected={false}
                preventResetOnSelected
                isSearchable={false}
                dropdownHeight={130}
                dropdownPosition="right"
                showTickIcon={false}
            />
        );
    }, [isTypeFilterActive, typeFilter, onTypeFilterChange, typeOptionsForCountry]);

    const [windowWidth] = useWindowSizeDebounced(200);
    const scale = Math.min(windowWidth / 1600, 1);
    const colSize = useCallback((base: number, min: number = 120) => Math.max(Math.round(base * scale), min), [scale]);

    const dateColumnKey = useMemo(() => {
        if (orderBy === 'updateDate') return 'updateDate';
        return typeFilter?.length === 1 && typeFilter[0] === DocumentTypesEnum.Mac ? 'updateDate' : 'CreateDateToOrder';
    }, [orderBy, typeFilter]);

    const isMacOnly = useMemo(() => typeFilter?.length === 1 && typeFilter[0] === DocumentTypesEnum.Mac, [typeFilter]);

    const isAccessColumnNeeded = useMemo(() => data.some((pub) => !pub.hasAccess), [data]);

    const columns = useMemo<ColumnDef<iPublication>[]>(() => {
        const accessColumn: ColumnDef<iPublication> = {
            id: 'access',
            header: () => null,
            cell: (info) => {
                const publication = info.row.original;
                if (!publication.hasAccess) {
                    return (
                        <span className="Table__access-icon">
                            <IconDeny />
                        </span>
                    );
                }
                return null;
            },
            size: 5
        };

        const baseColumns: ColumnDef<iPublication>[] = [
            {
                accessorKey: 'title',
                header: () => (
                    <SortableHeader columnKey="Title" orderBy={orderBy} sortOrder={sortOrder} onSort={onSort}>
                        REPORT NAME
                    </SortableHeader>
                ),
                cell: (info) => {
                    const title = info.getValue() as string;
                    return (
                        <div className="Table__title-cell">
                            <span className="Table__title">{searchQuery ? highlightSearchText(title, searchQuery) : title}</span>
                        </div>
                    );
                },
                size: colSize(220, 300)
            },
            {
                accessorKey: 'description',
                header: () => (
                    <SortableHeader columnKey="Description" orderBy={orderBy} sortOrder={sortOrder} onSort={onSort}>
                        DESCRIPTION
                    </SortableHeader>
                ),
                cell: (info) => {
                    const publication = info.row.original;
                    if (!publication.hasAccess) {
                        return (
                            <div className="Table__description Table__description--restricted">
                                Unlock this with a Report Pro package
                            </div>
                        );
                    }
                    const description = info.getValue() as string;
                    if (!description) return <div className="Table__description">-</div>;

                    if (searchQuery && description.toLowerCase().includes(searchQuery.toLowerCase())) {
                        const { snippet, prefix, suffix } = getDescriptionSnippet(description, searchQuery);
                        return (
                            <div className="Table__description Table__description--expanded" title={description}>
                                {prefix}
                                {highlightSearchText(snippet, searchQuery)}
                                {suffix}
                            </div>
                        );
                    }

                    return (
                        <div className="Table__description" title={description}>
                            {searchQuery ? highlightSearchText(description, searchQuery) : description}
                        </div>
                    );
                },
                size: colSize(210, 180)
            },
            {
                accessorKey: 'publicationType',
                header: () => <TypeHeader />,
                cell: (info) => {
                    const type = info.getValue() as PublicationTypesEnum;
                    let typeLabel = 'Publication';
                    if (type === PublicationTypesEnum.Mac) typeLabel = 'MAC';
                    else if (type === PublicationTypesEnum.Chapter) typeLabel = 'Chapter';

                    return <span className={`Table__type Table__type--${typeLabel.toLowerCase()}`}>{typeLabel}</span>;
                },
                size: colSize(180, 140)
            },
            {
                accessorKey: 'createdDate',
                header: (info) => (
                    <SortableHeader
                        columnKey={dateColumnKey}
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        defaultOrder="desc"
                    >
                        <>
                            <span className="Table__header-date-top">DATE</span>
                            <span className="Table__header-date-bottom">PUBLISHED</span>
                        </>
                    </SortableHeader>
                ),
                cell: (info) => {
                    const date = info.getValue() as string;
                    if (!date) return '-';
                    const formattedDate = new Date(date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    });
                    return <span>{formattedDate}</span>;
                },
                size: colSize(170, 140)
            },
            {
                accessorKey: 'numberOfPages',
                header: () => (
                    <SortableHeader
                        columnKey="NumberOfPages"
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        defaultOrder="desc"
                    >
                        PAGES
                    </SortableHeader>
                ),
                cell: (info) => {
                    const pages = info.getValue() as number;
                    return <span className="Table__page-count">{pages}</span>;
                },
                size: colSize(80, 70)
            },
            {
                accessorKey: 'minutesToRead',
                header: () => (
                    <SortableHeader
                        columnKey="minutesToRead"
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        defaultOrder="desc"
                    >
                        READ LENGTH
                    </SortableHeader>
                ),
                cell: (info) => {
                    const minutes = info.getValue() as number;
                    return <span className="Table__minutes-to-read">{minutes} min read</span>;
                },
                size: colSize(180, 140)
            },
            {
                accessorKey: 'tags',
                header: () => (
                    <div className="Table__tags-header">
                        <span>Tags</span>
                        <small className="Table__tags-header-small">click to drill down</small>
                    </div>
                ),
                cell: (info: CellContext<iPublication, unknown>) => {
                    const tags = info.getValue() as iPublication['tags'];
                    const publication = info.row.original;
                    const isExpanded = expandedRows.has(publication.id);
                    const handleOnTagClick = (tag: iTag) => {
                        if (isMacOnly) {
                            return;
                        }
                        onTagClick?.(tag);
                    };

                    return (
                        <>
                            <TagsCell
                                rowId={publication.id}
                                tags={tags}
                                isExpanded={isExpanded}
                                toggleRowExpansion={toggleRowExpansion}
                                onTagClick={handleOnTagClick}
                            />
                            {!publication.hasAccess && (
                                <Button className="Table__access-request-wrapper" onClick={(e) => e.stopPropagation()} unstyled>
                                    <RequestAccessButton
                                        type="Button"
                                        className="RequestAccessButton--small"
                                        articleId={publication.id}
                                        documentType={publication.publicationType}
                                        text="Request access"
                                    />
                                </Button>
                            )}
                        </>
                    );
                },
                size: colSize(460, 250)
            }
        ];

        return isAccessColumnNeeded ? [accessColumn, ...baseColumns] : baseColumns;
    }, [
        isAccessColumnNeeded,
        colSize,
        orderBy,
        sortOrder,
        onSort,
        searchQuery,
        TypeHeader,
        expandedRows,
        onTagClick,
        toggleRowExpansion,
        typeFilter,
        dateColumnKey,
        isMacOnly
    ]);

    return (
        <DataTable<iPublication>
            data={data}
            columns={columns}
            onRowClick={onRowClick}
            stickyTopOffset={stickyTopOffset}
            isLoading={isLoading}
            emptyText="No Results found"
            showBottomLoader={data.length > 0 && isLoading}
            showOverlayLoader={(data.length === 0 && isLoading) || isTypeChanging}
            wrapperClassName={isAccessColumnNeeded ? 'Table--with-lock' : undefined}
        />
    );
};

export default Table;
